import { baseApi } from 'services/api';
import { API_BASE_URL, API_TAG_TYPES, API_VERSION } from 'constants/common';
import {
  AddCustomerAddressPayload,
  CreateCustomerPayload,
  CustomerAddress,
  CustomerCard,
  CustomerCardSetupIntentPayload,
  CustomerCardSetupIntentResponseData,
  CustomerViewListResponse,
  DeleteCustomerCardPayload,
  EditCustomerAddressPayload,
  GetCustomerCardByIdPayload,
  GetCustomerCardsPayload,
  GetCustomerEstimatesResponse,
  GetCustomersPayload,
  GetCustomersResponse,
  GetCustomersV2Payload,
  GetCustomersV2Response,
  GetRecentCustomersPayload,
  SaveCustomerCardPayload,
  UpdateCustomerPayload,
  UpdateCustomerViewPriorityPayload,
  ValidateAddressPayload
} from './customers.model';
import { FileTypes } from 'types/baseTypes';
import { downloadFile } from 'utils/commonUtils';
import { CustomerV2QueryParams } from './hooks/usePaginatedGetCustomersV2Query';

const CustomersBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getCustomersList: builder.query<GetCustomersResponse, GetCustomersPayload>({
      query: params => {
        let url = `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/teams/customers?`;
        const urlParams = new URLSearchParams();
        Object.keys(params).map(key => {
          if (Array.isArray(params[key])) {
            params[key].map(item => {
              urlParams.append(key, item);
            });
          } else {
            if (params[key] !== null) {
              urlParams.append(key, params[key]);
            }
          }
        });
        url = url + urlParams;
        return {
          url: url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch customers data',
        showToast: true
      }
    }),
    getCustomersListV2: builder.query<GetCustomersV2Response, GetCustomersV2Payload>({
      query: ({ teamId, ...params }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/customers`,
        method: 'GET',
        params: params
      }),
      providesTags: [API_TAG_TYPES.CUSTOMERS],
      extraOptions: {
        failure: 'Failed to fetch customers data',
        showToast: true
      }
    }),
    createCustomer: builder.mutation<any, CreateCustomerPayload>({
      query: payload => {
        return {
          url: `${API_VERSION.V4}/merchants/customers`,
          method: 'POST',
          body: payload
        };
      },
      invalidatesTags: [API_TAG_TYPES.CUSTOMERS],
      extraOptions: {
        success: 'Customer created successfully'
      }
    }),
    updateCustomer: builder.mutation<any, UpdateCustomerPayload>({
      query: ({ customerId, ...payload }) => {
        return {
          url: `${API_VERSION.V4}/merchants/customers/${customerId}`,
          method: 'PUT',
          body: payload
        };
      },
      invalidatesTags: [API_TAG_TYPES.CUSTOMERS],
      extraOptions: {
        success: 'Customer updated successfully'
      }
    }),
    getRecentSearchedCustomers: builder.query<GetCustomersResponse, GetRecentCustomersPayload>({
      query: ({ teamId }) => {
        const url = `${API_VERSION.V2}/merchants/recent-customers?teamId=${teamId}`;
        return {
          url: url,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'failed to fetch recent customers ',
        showToast: true
      }
    }),
    addCustomerAddress: builder.mutation<CustomerAddress, AddCustomerAddressPayload>({
      query: payload => {
        const url = `${API_VERSION.V1}/merchants/customers/address`;
        return {
          url: url,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Failed to add customer address',
        showToast: true
      }
    }),
    editCustomerAddress: builder.mutation<CustomerAddress, EditCustomerAddressPayload>({
      query: ({ id, body }) => {
        const url = `${API_VERSION.V1}/merchants/customers/address/${id}`;
        return {
          url: url,
          method: 'PUT',
          body: body
        };
      },
      extraOptions: {
        failure: 'Failed to edit customer address',
        showToast: true
      }
    }),
    getCitiesOfState: builder.query<string[], string>({
      query: state => {
        const url = `${API_VERSION.V1}/merchants/state/${state}/cities`;
        return {
          url: url,
          method: 'GET'
        };
      }
    }),
    getCustomerEstimates: builder.query<GetCustomerEstimatesResponse, { teamId: number; customerId: string }>({
      query: ({ teamId, customerId }) => {
        return {
          url: `${API_BASE_URL.ESTIMATE_MANAGER}/${API_VERSION.V1}/teams/${teamId}/estimates/customer/${customerId}`,
          method: 'GET'
        };
      },
      extraOptions: {
        failure: 'Failed to fetch customer estimates',
        showToast: true
      }
    }),
    validateAddress: builder.mutation<string, ValidateAddressPayload>({
      query: payload => {
        return {
          url: `${API_VERSION.V1}/merchants/validate-address`,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Please enter a valid city.',
        showToast: false
      }
    }),
    createCustomerCardSetupIntent: builder.mutation<
      CustomerCardSetupIntentResponseData,
      CustomerCardSetupIntentPayload
    >({
      query: ({ teamId, customerId, paymentMethodId = '' }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/customers/${customerId}/cards/setup-intent`,
        method: 'POST',
        body: {
          paymentMethodId
        }
      }),
      extraOptions: {
        failure: 'Failed to load',
        showToast: true
      }
    }),
    saveCustomerCard: builder.mutation<CustomerCard, SaveCustomerCardPayload>({
      query: ({ teamId, customerId, paymentMethodId, cardHolderName, email }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/customers/${customerId}/cards`,
        method: 'POST',
        body: {
          paymentMethodId,
          cardHolderName,
          email
        }
      }),
      invalidatesTags: [API_TAG_TYPES.CUSTOMER_CARDS, API_TAG_TYPES.CUSTOMERS],
      extraOptions: {
        success: 'Card saved successfully',
        failure: 'Failed to save card',
        showToast: false
      }
    }),
    getCustomerCards: builder.query<CustomerCard[], GetCustomerCardsPayload>({
      query: ({ teamId, customerId }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/customers/${customerId}/cards`,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.CUSTOMER_CARDS],
      extraOptions: {
        failure: 'Failed to fetch customer cards',
        showToast: true
      }
    }),
    getCustomerCardById: builder.query<CustomerCard, GetCustomerCardByIdPayload>({
      query: ({ teamId, customerId, cardId }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/customers/${customerId}/cards/${cardId}`,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.CUSTOMER_CARDS],
      extraOptions: {
        failure: 'Failed to fetch customer card details',
        showToast: true
      }
    }),
    deleteCustomerCard: builder.mutation<CustomerCard, DeleteCustomerCardPayload>({
      query: ({ teamId, customerId, cardId }) => {
        return {
          url: `${API_VERSION.V1}/teams/${teamId}/customers/${customerId}/cards/${cardId}`,
          method: 'DELETE'
        };
      },
      invalidatesTags: [API_TAG_TYPES.CUSTOMER_CARDS, API_TAG_TYPES.CUSTOMERS],
      extraOptions: {
        success: 'Card deleted successfully',
        failure: 'Failed to delete card',
        showToast: true
      }
    }),
    getCustomerViews: builder.query<CustomerViewListResponse, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/customers/views`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch customer views',
        showToast: true
      }
    }),
    updateCustomerViewPriority: builder.mutation<void, UpdateCustomerViewPriorityPayload>({
      query: ({ teamId, order }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/customers/views`,
        method: 'PUT',
        body: { order }
      }),
      extraOptions: {
        success: 'View order updated successfully',
        failure: 'Failed to update view order',
        showToast: true
      }
    }),
    downloadCustomerList: builder.query<any, { teamId: string; params: CustomerV2QueryParams }>({
      query: ({ teamId, params }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/customers/export`,
        method: 'GET',
        params: params,
        responseHandler: async response => {
          await downloadFile({ response, fileType: FileTypes.CSV, fileName: 'customers' });
        }
      }),
      extraOptions: {
        failure: 'Failed to export customers',
        success: 'Customers exported successfully',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetCustomersListQuery,
  useLazyGetCustomersListV2Query,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useLazyGetRecentSearchedCustomersQuery,
  useAddCustomerAddressMutation,
  useLazyGetCitiesOfStateQuery,
  useEditCustomerAddressMutation,
  useLazyGetCustomerEstimatesQuery,
  useValidateAddressMutation,
  useLazyGetCustomerCardsQuery,
  useLazyGetCustomerCardByIdQuery,
  useDeleteCustomerCardMutation,
  useCreateCustomerCardSetupIntentMutation,
  useSaveCustomerCardMutation,
  useGetCustomerViewsQuery,
  useUpdateCustomerViewPriorityMutation,
  useLazyDownloadCustomerListQuery
} = CustomersBaseApi;
